import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { UserKeys, getUsersByOrgUnit } from '../../../api/users-api';
import { useNavigate } from 'react-router-dom';
import OrgUnitsGridToolbar from './org-units-grid-toolbar';
import { User } from '../../../../types';
import SetOrgsDialog from '../../user-management/user-grid-actions/users/set-orgs-dialog';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import UserAvatar from '../../../shared/user-avatar';
import { pagesConfig } from '../../../shared/pages-config';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { useUserSettings } from '../../../utils/use-user-settings';
import StatusLabel from '../../../shared/status-label';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PersonOff from '@mui/icons-material/PersonOff';

interface OrgUnitsGridProps {
  selectedOrgUnitPath: string;
  primaryDomain: string;
  userRoles: string[];
  changeLogRoles: string[];
}

export default function OrgUnitsGrid({
  selectedOrgUnitPath,
  primaryDomain,
  userRoles,
  changeLogRoles,
}: OrgUnitsGridProps) {
  const navigate = useNavigate();
  const { viewType } = useUserSettings();
  const [openMoveUserDialog, setOpenMoveUserDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'primaryEmail',
        headerName: 'Email',
        editable: false,
        sortable: true,
        filterable: false,
        flex: 2,
        display: 'flex',
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <UserAvatar
                userEmail={params.row.primaryEmail}
                sx={{ width: 36, height: 36 }}
              />
              <Typography variant="body2">{params.row.primaryEmail}</Typography>
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'givenName',
        headerName: 'First Name',
        editable: false,
        sortable: true,
        filterable: false,
        flex: 1,
      },
      {
        field: 'familyName',
        headerName: 'Last Name',
        editable: false,
        sortable: true,
        filterable: false,
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        editable: false,
        sortable: false,
        filterable: false,
        width: 120,
        renderCell: (params) => {
          const status = params.row.archived
            ? 'ARCHIVED'
            : params.row.suspended
            ? 'SUSPENDED'
            : 'ACTIVE';
          return <StatusLabel status={status} />;
        },
      },
      {
        field: 'jobTitle',
        headerName: 'Job Title',
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
      },
      {
        field: 'orgUnitName',
        headerName: 'Org Unit Name',
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          if (row.orgUnitPath === null) return '';
          if (row.orgUnitPath === '/') return primaryDomain;
          return row.orgUnitPath.split('/').slice(-1)[0];
        },
      },
      {
        field: 'orgUnitPath',
        headerName: 'Org Unit Path',
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          if (row.orgUnitPath === null) return '';
          if (row.orgUnitPath === '/') return '/' + primaryDomain;
          return row.orgUnitPath;
        },
      },
    ],
    [primaryDomain]
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'orgUnitsGridStates'
  );

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        orgUnitPath: false,
      },
    },
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 25,
      },
      rowCount: 0,
    },
  };

  const columnSorting: Record<string, string> = {
    primaryEmail: 'EMAIL',
    givenName: 'GIVEN_NAME',
    familyName: 'FAMILY_NAME',
    asc: 'ASCENDING',
    desc: 'DESCENDING',
  };
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    column: 'EMAIL' | 'GIVEN_NAME' | 'FAMILY_NAME' | null;
    sortDirection: 'ASCENDING' | 'DESCENDING' | null;
    search: string;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 25,
    page: 0,
    cursor: null,
    column:
      (columnSorting[initialState?.sorting?.sortModel?.[0]?.field || ''] as
        | 'EMAIL'
        | 'GIVEN_NAME'
        | 'FAMILY_NAME') || null,
    sortDirection:
      (columnSorting[initialState?.sorting?.sortModel?.[0]?.sort || ''] as
        | 'ASCENDING'
        | 'DESCENDING') || null,
    search: initialState?.filter?.filterModel?.quickFilterValues?.[0] || '',
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.OrgUnitUsers,
      selectedOrgUnitPath,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.column,
      paginationModel.sortDirection,
      paginationModel.search,
    ],
    queryFn: () =>
      getUsersByOrgUnit(
        //  Escape single quotes in queries with \'
        `'${selectedOrgUnitPath.replace(/'/g, "\\'")}'`,
        paginationModel.search,
        paginationModel.column as 'EMAIL' | 'GIVEN_NAME' | 'FAMILY_NAME',
        paginationModel.sortDirection as 'ASCENDING' | 'DESCENDING',
        paginationModel.pageSize,
        paginationModel.cursor || ''
      ),
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading users. Please refresh and try again.',
    },
  });

  const toolbarProps = useMemo(
    () => ({
      paginationModel,
      setPaginationModel,
      setOpenMoveUserDialog,
      selectedRows,
      userRoles,
    }),
    [paginationModel, selectedRows, userRoles]
  );

  const rowData = useMemo(
    () =>
      data?.results?.map((user) => {
        const primaryOrg =
          user.organizations?.find((o) => o.primary) ?? user.organizations?.[0];
        return {
          ...user,
          id: user.id,
          primaryEmail: user.primaryEmail,
          givenName: user.name?.givenName,
          familyName: user.name?.familyName,
          jobTitle: primaryOrg?.title ?? '',
          orgUnitPath: user.orgUnitPath,
          suspended: user.suspended,
          archived: user.isArchived,
        };
      }),
    [data?.results]
  );

  return (
    <>
      <SharedDataGridServer
        gridKey={`${
          viewType.orgUnitsGridStates?.activeGridState || 'default'
        }-${selectedOrgUnitPath}`}
        columns={computedColumns}
        data={data}
        rows={rowData || []}
        getRowId={(row) => row.primaryEmail}
        slots={{
          toolbar: useToolbar(OrgUnitsGridToolbar, toolbarProps),
          ...createGridOverlays(
            'Users',
            <PersonOff />,
            'There may be no users in this organizational unit or the search filter is too restrictive.'
          ),
        }}
        initialState={initialState || defaultGridState}
        checkboxSelection
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        loading={isLoading || isFetching}
        paginationModel={{
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
        }}
        onPaginationModelChange={(newModel) => {
          if (isFetching || isLoading) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              column: paginationModel.column,
              sortDirection: paginationModel.sortDirection,
              search: paginationModel.search,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: data?.cursor || '',
              column: paginationModel.column,
              sortDirection: paginationModel.sortDirection,
              search: paginationModel.search,
            });
          }
        }}
        onSortModelChange={(newModel) => {
          if (isFetching || isLoading) return;

          let column: 'EMAIL' | 'GIVEN_NAME' | 'FAMILY_NAME' | null = null;
          if (newModel.length > 0) {
            switch (newModel[0].field) {
              case 'primaryEmail':
                column = 'EMAIL';
                break;
              case 'givenName':
                column = 'GIVEN_NAME';
                break;
              case 'familyName':
                column = 'FAMILY_NAME';
                break;
              default:
                break;
            }
          }

          setPaginationModel({
            page: 0,
            pageSize: paginationModel.pageSize,
            cursor: null,
            column: column,
            sortDirection:
              newModel.length > 0 && newModel[0].sort === 'asc'
                ? 'ASCENDING'
                : newModel.length > 0 && newModel[0].sort === 'desc'
                ? 'DESCENDING'
                : null,
            search: paginationModel.search,
          });
        }}
        onFilterModelChange={(model) => {
          if (isFetching || isLoading) return;
          const search = model.quickFilterValues?.[0] || '';

          setPaginationModel((prev) => ({
            ...prev,
            page: 0,
            cursor: null,
            search,
          }));
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onRowClick={(row: GridRowParams<User>) => {
          navigate(`/${pagesConfig.users.link}/${row.row.primaryEmail}`);
        }}
      />
      {openMoveUserDialog && (
        <SetOrgsDialog
          open={openMoveUserDialog}
          handleClose={() => setOpenMoveUserDialog(false)}
          selectedUsers={selectedRows}
          setSelectedUsers={setSelectedRows}
          userRoles={userRoles}
          changeLogRoles={changeLogRoles}
        />
      )}
    </>
  );
}
